import { useFormikContext } from 'formik'
import { useEffect } from 'react'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import { FormattedMessage } from 'react-intl'

import { AdministrativeDivisionInputField } from '@components/Form/AdministrativeDivisionInput'
import { InputField } from '@components/Form/Input'
import { PostCodeInputField } from '@components/Form/PostalCodeInput'
import { FormControl } from '@components/FormControl'

import { CountrySelectField } from '@modules/country/components/CountrySelectField'

import { PatientRelativeFormShape } from '../PatientRelativeForm'

const PatientRelativeFormNricAddressField = () => {
  const { values, errors, setFieldValue } = useFormikContext<PatientRelativeFormShape>()

  useEffect(() => {
    if (values.is_same_address) {
      setFieldValue('living_country', values.nric_country)
      setFieldValue('living_first_level_administrative_division_id', values.nric_first_level_administrative_division_id)
      setFieldValue(
        'living_second_level_administrative_division_id',
        values.nric_second_level_administrative_division_id
      )
      setFieldValue('living_third_level_administrative_division_id', values.nric_third_level_administrative_division_id)
      setFieldValue(
        'living_fourth_level_administrative_division_id',
        values.nric_fourth_level_administrative_division_id
      )
      setFieldValue('living_postal_code', values.nric_postal_code)
      setFieldValue('living_address_1', values.nric_address_1)
      setFieldValue('living_address_2', values.nric_address_2)
      setFieldValue('living_rt', values.nric_rt)
      setFieldValue('living_rw', values.nric_rw)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.is_same_address,
    values.nric_country,
    values.nric_first_level_administrative_division_id,
    values.nric_second_level_administrative_division_id,
    values.nric_third_level_administrative_division_id,
    values.nric_fourth_level_administrative_division_id,
    values.nric_postal_code,
    values.nric_address_1,
    values.nric_address_2,
    values.nric_rt,
    values.nric_rw,
  ])

  return (
    <>
      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="patient.address" />
        </FormControl.Label>
        <Stack gap={3}>
          <InputField name="nric_address_1" />
          <InputField name="nric_address_2" />
        </Stack>
        <FormControl.ErrorMessage message={errors.nric_address_1 || errors.nric_address_2} />
      </FormControl>

      <Row sm={2}>
        <Col
          md={6}
          lg={4}
          xl={3}
        >
          <FormControl>
            <FormControl.Label>
              <FormattedMessage id="vocabulary.rt" />
            </FormControl.Label>
            <InputField name="nric_rt" />
            <FormControl.ErrorMessage message={errors.nric_rt} />
          </FormControl>
        </Col>
        <Col
          md={6}
          lg={4}
          xl={3}
        >
          <FormControl>
            <FormControl.Label>
              <FormattedMessage id="vocabulary.rw" />
            </FormControl.Label>
            <InputField name="nric_rw" />
            <FormControl.ErrorMessage message={errors.nric_rw} />
          </FormControl>
        </Col>
      </Row>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="country" />
        </FormControl.Label>

        <CountrySelectField
          name="nric_country"
          onChange={() => {
            setFieldValue('nric_first_level_administrative_division_id', undefined)
          }}
        />
        <FormControl.ErrorMessage message={errors.nric_country} />
      </FormControl>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="province" />
        </FormControl.Label>

        <AdministrativeDivisionInputField
          name="nric_first_level_administrative_division_id"
          countryId={values.nric_country?.data?.id}
          onChange={() => {
            setFieldValue('nric_second_level_administrative_division_id', undefined)
          }}
        />
        <FormControl.ErrorMessage message={errors.nric_first_level_administrative_division_id} />
      </FormControl>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="city" />
        </FormControl.Label>

        <AdministrativeDivisionInputField
          name="nric_second_level_administrative_division_id"
          countryId={values.nric_country?.data?.id}
          parentId={values.nric_first_level_administrative_division_id?.value ?? null}
          onChange={() => {
            setFieldValue('nric_third_level_administrative_division_id', undefined)
          }}
        />
        <FormControl.ErrorMessage message={errors.nric_second_level_administrative_division_id} />
      </FormControl>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="district" />
        </FormControl.Label>

        <AdministrativeDivisionInputField
          name="nric_third_level_administrative_division_id"
          countryId={values.nric_country?.data?.id}
          parentId={values.nric_second_level_administrative_division_id?.value ?? null}
          onChange={() => {
            setFieldValue('nric_fourth_level_administrative_division_id', undefined)
          }}
        />
        <FormControl.ErrorMessage message={errors.nric_third_level_administrative_division_id} />
      </FormControl>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="sub_district" />
        </FormControl.Label>

        <AdministrativeDivisionInputField
          name="nric_fourth_level_administrative_division_id"
          countryId={values.nric_country?.data?.id}
          parentId={values.nric_third_level_administrative_division_id?.value ?? null}
        />
        <FormControl.ErrorMessage message={errors.nric_fourth_level_administrative_division_id} />
      </FormControl>

      <FormControl>
        <FormControl.Label>
          <FormattedMessage id="postal_code" />
        </FormControl.Label>
        <PostCodeInputField name="nric_postal_code" />
        <FormControl.ErrorMessage message={errors.nric_postal_code} />
      </FormControl>
    </>
  )
}

export { PatientRelativeFormNricAddressField }
